import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {Modifier, ModifierGroup} from '../../api/types';
import {useRouter} from '../../app/router';
import {ROUTES} from '../../app/routes';
import {useCart} from '../../cart';
import {reasonMessageKey, useI18n} from '../../i18n';
import type {MessageKey} from '../../i18n';
import {useMenu} from '../../menu';
import {
  ActionLink,
  BackLink,
  Button,
  Money,
  MoneyDelta,
  OptionGroup,
  OptionRow,
  Photo,
  QtyStepper,
  Screen,
  ScreenBody,
  ScreenFooter,
  SectionHeader,
  SkeletonBar,
  SkeletonRow,
  StatusPill,
  TerminalNotice,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q4 · Item detail and modifiers — `/menu/:itemId`
 *
 * Spec: `03-qr-ordering.md` §Q4. Design package: the item-detail screen.
 * Foundations: `00-foundations.md` and `src/ui`.
 *
 * ## Three decisions worth knowing before reading the code
 *
 * **1. Every surface decision comes from `src/ui`, and none from here.** This
 * screen used to be built out of one inline style object and a private
 * stylesheet, on the argument that a token-only surface needs no components.
 * That argument was right about *tokens* and wrong about *count*: thirteen
 * screens each holding their own idea of a card, a 48px control and a 16px floor
 * is how a design system becomes four slightly different greys. So the shell,
 * the option rows, the stepper, the money figures and the button weights are all
 * the shared primitives now, and the only CSS below is the handful of layout
 * rules that belong to this screen alone. A consequence worth keeping in view:
 * **Pandan still cannot reach this screen.** Every figure here goes through
 * `Money` with the default neutral tone, and there is no path from an item
 * detail screen to `--pokpok-color-money-verified` — the guest has not paid, and
 * at a full-service venue will not pay for another hour.
 *
 * **2. The no-photo case is the layout, not a branch.** Most V1 merchants have
 * no photography (foundations §14), so the head of this screen is typographic:
 * name at the title step, price beneath it, description below that. It is
 * complete and it is the design. A photo, when one exists *and* loads, is
 * inserted above that block as an enhancement — and a URL that 404s or times out
 * on 4G removes itself and leaves the designed state behind rather than a
 * broken-image glyph. There is no grey placeholder box: `test/guest-path.test.tsx`
 * asserts that an item with no `image_url` renders no image element at all, and
 * the design package's striped box is exactly what that forbids. The stripe
 * survives only as the background *behind* a real `<img>`, which is what `Photo`
 * paints while the bytes are in flight.
 *
 * **3. Sold out is a moment, never a validation error.** `isAvailable` is live
 * and can flip while this screen is open (design principle #2 — the visible edge
 * of the one-menu promise). When it does, the whole configure-and-add form is
 * replaced by an apology, the reason the kitchen gave, and one way out. It is
 * not a red banner over a form the guest can still fill in and be refused at
 * checkout. The same treatment covers arriving at an already-sold-out item from
 * a stale menu or a shared link; only the wording differs, because "just sold
 * out" is a lie about an item that has been off since lunch.
 *
 * ## The add button is disabled until the required groups are answered
 *
 * It used to stay live and scroll to the group that was waiting, on the
 * reasoning that a disabled primary button on a phone is a dead end — the guest
 * taps, nothing happens, and nothing on screen says why. That reasoning was
 * sound and its premise is now false: `Button` renders a disabled control with
 * `aria-disabled` rather than the `disabled` attribute, so it stays focusable,
 * and `disabledReason` puts the sentence on the screen underneath it *and* in
 * the button's accessible description. The question is answered where the guest
 * is looking instead of by moving the page under them, which is the design's own
 * behaviour (a desaturated `--pokpok-color-accent-disabled` fill, with the price
 * still shown) minus the part the design got wrong.
 *
 * ## Adding commits the line and leaves
 *
 * The add button writes the line and navigates to Q5. This screen used to stay
 * put instead: an `added` flag swapped the button for a confirmation line and a
 * two-link footer ("back to the menu" / "view the cart"), on the reasoning that
 * a guest ordering four dishes should not have to walk back from the cart three
 * times. The design package answers that differently and the owner has ruled for
 * the package — `dc.html` commits the line and sets `screen:'cart', stack:[]` in
 * the same `setState`, and Q5 is where "keep ordering" lives, one tap away, with
 * the whole order visible while the guest decides. The old shape also left the
 * screen with two personalities: everything below the fold — the modifier rows,
 * the note, the stepper — went on being editable behind a footer that claimed
 * the line was already committed, and every one of those controls had to clear
 * the flag to stay honest. Those `setAdded(false)` calls are gone with it.
 *
 * What survives the move is the **announcement**. A navigation is silent, so a
 * guest using a screen reader gets no signal that the tap did anything except a
 * new page they have to read to find out; see `announceAdded` below for why it
 * cannot be a live region rendered by this component.
 *
 * ## What this screen deliberately does not do
 *
 * No payment, no checkout, no order total. The running figure is a menu price
 * plus modifier deltas; the only authoritative money in this product is
 * `Order.totals` (see `cartModel.ts`). No key is hard-coded in any language:
 * Thai is the source and every string comes through `t()` / `ln()`.
 */

/**
 * Whether a guest may write free text to the kitchen at all.
 *
 * **This is an OPEN product question** (03 §Q4: "Whether free-text notes are
 * allowed at all from guests"), and it is a single constant precisely so that
 * answering it either way is a one-line change and not a redesign. Flip it to
 * `false` and the field is replaced by `q4.noteUnavailable` — the section keeps
 * its shape, nothing below it moves, and the guest is told rather than left to
 * wonder where the box went.
 *
 * It is a build-time constant rather than venue configuration because **the
 * frozen contract carries no field for it**: nothing on `GuestSession`,
 * `ChannelMenu` or `ServiceStatus` says whether this merchant accepts guest
 * notes. When that field lands, this constant becomes the read of it. Until
 * then, inventing a per-venue toggle would mean inventing a data source.
 */
const GUEST_KITCHEN_NOTES_ENABLED = true;

/**
 * A ticket read at three metres by someone who may not read the guest's
 * language cannot carry a paragraph. This is a legibility guard on the KDS
 * side, not a business rule, which is why it is silent — the field simply stops
 * accepting more.
 */
const NOTE_MAX_LENGTH = 140;


/** Chosen modifiers: group id → modifier id → quantity. */
type Selections = Record<string, Record<string, number>>;

/**
 * A modifier a guest may actually pick right now.
 *
 * `active` is the merchant's switch and `availability` is the kitchen's. Either
 * one closes the option, and neither is an error — they are the same one-menu
 * fact the item itself carries.
 */
function isModifierSelectable(modifier: Modifier): boolean {
  if (modifier.active === false) return false;
  return modifier.availability?.available !== false;
}

/**
 * How many things are chosen in a group.
 *
 * Quantities, not rows: with `allow_repeat`, "เลือกได้ไม่เกิน 3" means three
 * shots of espresso, not three distinct kinds of one.
 */
function groupCount(chosen: Record<string, number> | undefined): number {
  if (!chosen) return 0;
  return Object.values(chosen).reduce((sum, quantity) => sum + quantity, 0);
}

/**
 * The same count, but over the raw selection state rather than `effective`.
 *
 * `effective` has already dropped anything that sold out while it was ticked, so
 * the number the group displays counts only selectable modifiers. The state
 * updaters below hold the *raw* map, and a guard that counted that would refuse
 * a choice the guest can see is still open — so it filters the same way here.
 */
function selectableGroupCount(
  group: ModifierGroup,
  chosen: Record<string, number> | undefined,
): number {
  if (!chosen) return 0;
  let total = 0;
  for (const modifier of group.modifiers) {
    if (isModifierSelectable(modifier)) total += chosen[modifier.modifier_id] ?? 0;
  }
  return total;
}

/**
 * The rule line under a group name, in words rather than as `min/max`.
 *
 * `min_select >= 1` is what makes a group required (the contract says so
 * outright). The four shapes below are the four the contract can express; a
 * group whose max is below its min is malformed data, and "choose at least N"
 * is the reading that still lets a guest finish ordering.
 */
function selectionRule(group: ModifierGroup): {key: MessageKey; params: Record<string, number>} {
  const {min_select: min, max_select: max} = group.selection;
  if (min > 0 && max === min) return {key: 'q4.chooseExactly', params: {count: min}};
  if (min > 0 && max > min) return {key: 'q4.chooseBetween', params: {min, max}};
  if (min > 0) return {key: 'q4.chooseAtLeast', params: {count: min}};
  return {key: 'q4.chooseUpTo', params: {count: Math.max(max, 1)}};
}

/**
 * The live region that says the line landed, in a node that outlives this screen.
 *
 * Adding navigates to Q5, so anything this component renders is unmounted in the
 * same commit — a `role="status"` in the footer would be torn out of the
 * accessibility tree before any screen reader read it, and a live region has to
 * already be *in* that tree when its text changes or the change is not a change
 * to announce. So the node is created once, appended to `document.body`, and
 * left there for the life of the tab, which is what every published announcer
 * implementation does for the same reason. It is `.pokpok-sr-only`, so it is
 * announced and never seen.
 *
 * The empty-then-fill in two tasks is the other half of the same rule: on the
 * first add the region has only just been attached, and on the second the text
 * would otherwise be identical to what is already sitting there — which several
 * screen readers treat as nothing having happened. Clearing it and writing the
 * sentence back on the next tick makes both cases a real mutation.
 *
 * This is the third screen to want a persistent announcer (Q7 and Q8 both have
 * live regions that survive only because those screens do not navigate on
 * success) and it belongs in `src/ui` the moment a fourth appears. It is written
 * here because it is one screen's need today and inventing a foundation
 * primitive for one caller is how `src/ui` grows things nobody else can use.
 */
let announcer: HTMLElement | null = null;
let announcerTimer: ReturnType<typeof setTimeout> | undefined;

function announceAdded(message: string) {
  if (typeof document === 'undefined') return;
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.className = 'pokpok-sr-only';
    document.body.append(announcer);
  }
  const node = announcer;
  node.textContent = '';
  clearTimeout(announcerTimer);
  announcerTimer = setTimeout(() => {
    node.textContent = message;
  }, 0);
}

export default function Q04ItemDetail() {
  const {t, ln, money} = useI18n();
  const {params, navigate} = useRouter();
  const {itemsById, status: menuStatus, isAvailable, availabilityOf, reload} = useMenu();
  const cart = useCart();

  const itemId = params.itemId ?? '';
  const item = itemsById.get(itemId);
  const available = isAvailable(itemId);

  const [selections, setSelections] = useState<Selections>({});
  /*
   * One portion per add, always (owner directive, 2026-08-14). Kept as a named
   * constant rather than inlined at the two call sites below, because it is the
   * *reason* the running total and the submitted line agree — and because the
   * day a quantity control comes back, it comes back here.
   */
  const quantity = 1;
  const [note, setNote] = useState('');

  /**
   * Was this item on sale at any point while the guest had the screen open?
   *
   * It is the difference between "sorry, that just went" and an item that was
   * already off when they tapped it — a stale menu, a deep link, a shared URL.
   * Both get an apology; only one of them gets to claim it just happened.
   */
  const seenAvailable = useRef(false);
  useEffect(() => {
    if (available) seenAvailable.current = true;
  }, [available]);

  // A different item under the same screen is a different item. Without this a
  // guest who backs out of one dish and opens another inherits the first one's
  // modifiers and kitchen note. (Quantity is no longer state, so there is
  // nothing to reset — it is one portion per add.)
  useEffect(() => {
    setSelections({});
    setNote('');
    seenAvailable.current = false;
  }, [itemId]);

  const groups = useMemo(() => item?.modifier_groups ?? [], [item]);

  /**
   * The selections that still exist.
   *
   * A modifier can sell out while it is ticked. Silently keeping it would put a
   * line on the ticket the kitchen cannot make and price the item wrongly on
   * the way there; silently dropping it changes the guest's order without
   * telling them. So it is filtered out *here* — the price and the validation
   * never see it — while the row itself stays on screen, unticked and labelled
   * `q4.modifierUnavailable`. The guest sees the thing they chose, and sees
   * what happened to it.
   */
  const effective = useMemo<Selections>(() => {
    const next: Selections = {};
    for (const group of groups) {
      const chosen = selections[group.modifier_group_id];
      if (!chosen) continue;
      const kept: Record<string, number> = {};
      for (const modifier of group.modifiers) {
        const quantityChosen = chosen[modifier.modifier_id];
        if (quantityChosen && isModifierSelectable(modifier)) {
          kept[modifier.modifier_id] = quantityChosen;
        }
      }
      if (Object.keys(kept).length > 0) next[group.modifier_group_id] = kept;
    }
    return next;
  }, [groups, selections]);

  const unmetGroups = useMemo(
    () =>
      groups.filter(
        (group) => groupCount(effective[group.modifier_group_id]) < group.selection.min_select,
      ),
    [groups, effective],
  );

  /** Unit price plus the **signed** deltas. "ไม่เอาข้าว, −฿5" is a real modifier. */
  const unitSatang = useMemo(() => {
    if (!item) return 0;
    let total = item.price_satang;
    for (const group of groups) {
      const chosen = effective[group.modifier_group_id];
      if (!chosen) continue;
      for (const modifier of group.modifiers) {
        const count = chosen[modifier.modifier_id];
        if (count) total += modifier.price_delta_satang * count;
      }
    }
    return total;
  }, [item, groups, effective]);

  const runningSatang = unitSatang * quantity;

  const toggle = useCallback(
    (group: ModifierGroup, modifier: Modifier) => {
      const groupId = group.modifier_group_id;
      const single = group.selection.max_select === 1 && !group.selection.allow_repeat;
      setSelections((current) => {
        const chosen = {...(current[groupId] ?? {})};
        if (chosen[modifier.modifier_id]) {
          // Tapping the chosen option in a required single-select group is a
          // no-op, not a way to make the order invalid by accident. In an
          // OPTIONAL single-select group it is the only way to un-choose, and
          // this branch is reachable there because that group renders
          // checkboxes rather than radios — see `ModifierRow`.
          if (single && group.selection.min_select > 0) return current;
          delete chosen[modifier.modifier_id];
        } else if (single) {
          return {...current, [groupId]: {[modifier.modifier_id]: 1}};
        } else {
          // The rows are already disabled at the limit, so this is the second
          // lock rather than the first — but "the button was disabled" is a
          // claim about a render, and `max_select` is a claim about the order
          // that reaches the kitchen.
          if (selectableGroupCount(group, chosen) >= group.selection.max_select) {
            return current;
          }
          chosen[modifier.modifier_id] = 1;
        }
        return {...current, [groupId]: chosen};
      });
    },
    [],
  );

  const step = useCallback((group: ModifierGroup, modifier: Modifier, delta: number) => {
    const groupId = group.modifier_group_id;
    setSelections((current) => {
      const chosen = {...(current[groupId] ?? {})};
      // `max_select` counts quantities, not rows (see `groupCount`): under
      // `allow_repeat`, "เลือกได้ไม่เกิน 3" means three shots of espresso. So
      // the tap that has to be refused is a *second* tap on a row that is
      // already chosen, which is exactly the one a per-row "is this one picked"
      // guard never sees — the screen said เลือกครบแล้ว and the stepper kept
      // counting past it.
      if (delta > 0 && selectableGroupCount(group, chosen) >= group.selection.max_select) {
        return current;
      }
      const next = (chosen[modifier.modifier_id] ?? 0) + delta;
      if (next <= 0) delete chosen[modifier.modifier_id];
      else chosen[modifier.modifier_id] = next;
      return {...current, [groupId]: chosen};
    });
  }, []);

  const onAdd = useCallback(() => {
    if (!item) return;

    const modifiers: {modifier: Modifier; quantity: number}[] = [];
    for (const group of groups) {
      const chosen = effective[group.modifier_group_id];
      if (!chosen) continue;
      for (const modifier of group.modifiers) {
        const count = chosen[modifier.modifier_id];
        if (count) modifiers.push({modifier, quantity: count});
      }
    }

    const trimmed = note.trim();
    cart.add({
      item,
      quantity,
      modifiers,
      note: GUEST_KITCHEN_NOTES_ENABLED && trimmed !== '' ? trimmed : null,
    });

    // Said before the navigation, into a node the navigation does not unmount.
    // A guest who cannot see the cart slide in gets the same confirmation a
    // sighted guest gets from the screen changing under them.
    announceAdded(t('q4.added'));
    navigate(ROUTES.cart);
  }, [item, groups, effective, note, quantity, cart, t, navigate]);

  // --- states before the item exists ---------------------------------------

  if (!item) {
    // The projection is still arriving, or a `menu.updated` is being re-read.
    // Bars at the geometry of the real content — a title, a price, two lines of
    // description, two option rows — so nothing jumps when it lands. The design
    // package draws no loading state at all (not one spinner in 485 lines);
    // this is the shape `index.html`'s pre-bundle skeleton already established,
    // in the same grey, and deliberately not animated.
    if (menuStatus === 'idle' || menuStatus === 'loading') {
      return (
        <Shell>
          <ScreenBody>
            <div
              className="pk-stack pk-stack--wide"
              role="status"
              aria-busy="true"
              aria-label={t('common.loading')}>
              <SkeletonBar width="70%" height="28px" />
              <SkeletonBar width="30%" />
              <SkeletonBar />
              <SkeletonBar width="80%" />
              {[0, 1].map((row) => (
                <SkeletonRow key={row}>
                  <SkeletonBar width="55%" />
                  <SkeletonBar width="56px" />
                </SkeletonRow>
              ))}
            </div>
          </ScreenBody>
        </Shell>
      );
    }
    // Either the menu failed to load, or the item is genuinely not in it any
    // more — a shared link to a dish that has since been deleted. Both end the
    // same way: a sentence, a retry, and the menu.
    return (
      <Shell>
        <ScreenBody center>
          <TerminalNotice
            tone="error"
            glyph="!"
            title={t(menuStatus === 'error' ? 'error.generic' : 'error.notFound')}>
            <p className="pk-muted">{t('common.askStaff')}</p>
          </TerminalNotice>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          {menuStatus === 'error' ? (
            <Button variant="secondary" block onClick={reload}>
              {t('common.retry')}
            </Button>
          ) : null}
          {/*
            The menu is a genuine way forward from here, so it keeps the
            screen's one red. A retry that may fail again does not.
          */}
          <ActionLink to={ROUTES.menu} variant="primary">
            {t('q4.backToMenu')}
          </ActionLink>
        </ScreenFooter>
      </Shell>
    );
  }

  const name = ln(item.name);
  const description = ln(item.description);
  const availability = availabilityOf(itemId);
  const reasonKey = reasonMessageKey(availability?.reason);

  // --- 86'd: an apology with a way out, never a validation error ------------

  if (!available) {
    return (
      <Shell>
        <ScreenBody center>
          {/*
            Palm Sugar, not Dried Chili: nothing has gone wrong and nobody has
            made a mistake — the kitchen ran out, which is a fact about tonight.
          */}
          <TerminalNotice
            tone="warning"
            glyph="!"
            title={
              seenAvailable.current
                ? t('q4.soldOutWhileOpen', {item: name})
                : `${name} · ${t('common.soldOut')}`
            }>
            {reasonKey ? <p className="pk-muted">{t(reasonKey)}</p> : null}
            <p>{t('q4.soldOutAction')}</p>
          </TerminalNotice>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          <ActionLink to={ROUTES.menu} variant="primary">
            {t('q4.backToMenu')}
          </ActionLink>
        </ScreenFooter>
      </Shell>
    );
  }

  // --- the item ------------------------------------------------------------

  return (
    <Shell>
      {/*
        `scroll` makes the body its own scroll region, which is what pins the
        add bar below it by flex layout rather than by `position: fixed`. Fixed
        fights the collapsing URL bar and the on-screen keyboard, and this
        surface runs on a guest's own phone where both are unavoidable.
      */}
      <ScreenBody scroll>
        <article className="q4-article">
          <ItemPhoto src={item.image_url} />

          {/*
            Name and price on one line, price to the right (owner directive,
            2026-08-14) — the same row the menu list uses, so a guest who tapped
            a row finds the two facts they tapped in the same arrangement.

            `align-items: baseline` rather than centre: the dish name is the
            23.04px title step and the price is 16px, and centring them would
            float the figure in the middle of a two-line Thai name. Neutral tone,
            and structurally incapable of being anything else: a menu price is
            money-shaped with no settlement meaning (foundations §4).
          */}
          <header className="q4-head">
            <h1 className="pk-title q4-head-name">{name}</h1>
            {item.regular_price_satang != null && (
              // The ordinary price struck through beside the sale price (owner,
              // 2026-08-20). Inline because this screen carries no stylesheet of
              // its own for a one-off "was".
              <span
                style={{
                  textDecoration: 'line-through',
                  opacity: 0.6,
                  marginInlineEnd: 8,
                }}
              >
                <Money satang={item.regular_price_satang} />
              </span>
            )}
            <Money satang={item.price_satang} />
          </header>

          {/*
            River Stone, not ink. The design draws the description two type
            steps below the dish name, and this surface has no second step to
            draw it at — so the hierarchy that size used to carry is carried by
            colour. The absent case goes one shade quieter still: the design
            simply collapses the block, but a guest who is deciding between two
            dishes is owed the difference between "nothing to say" and "we
            forgot", and the two shades keep those apart at a glance.
          */}
          <section>
            <h2 className="pokpok-sr-only">{t('q4.description')}</h2>
            <p className={description ? 'pk-muted' : 'pk-hint'}>
              {description || t('q4.noDescription')}
            </p>
          </section>

          {groups.map((group) => (
            <ModifierGroupSection
              key={group.modifier_group_id}
              group={group}
              chosen={effective[group.modifier_group_id]}
              onToggle={toggle}
              onStep={step}
            />
          ))}

          <section className="q4-group">
            <SectionHeader title={t('q4.noteToKitchen')} />
            {GUEST_KITCHEN_NOTES_ENABLED ? (
              <>
                {/*
                  The honesty line sits ABOVE the field, not under it as a
                  disclaimer. 03 §Q4 asks for expectations to be set honestly,
                  and a caveat read after typing is a caveat read too late.
                */}
                <p className="pk-hint">{t('q4.noteHonesty')}</p>
                <textarea
                  className="pk-textarea"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={NOTE_MAX_LENGTH}
                  rows={2}
                  placeholder={t('q4.notePlaceholder')}
                  aria-label={t('q4.noteToKitchen')}
                />
              </>
            ) : (
              <p className="pk-muted">{t('q4.noteUnavailable')}</p>
            )}
          </section>

          {/*
            No quantity control here — owner directive, 2026-08-14, and the
            design package never drew one. Every add is one portion.

            The argument that used to sit here was that a table of four ordering
            the same dish is worse off tapping add four times. That is true and
            it is not this screen's problem to solve: the cart has a stepper on
            every line, the add now lands the guest *on* the cart, and a second
            identical configuration merges into the line already there rather
            than starting a new one. So the four-portion case costs one tap on
            the next screen, and this screen loses a control that was asking a
            question — how many? — before the guest had finished answering the
            one above it.
          */}
        </article>
      </ScreenBody>

      <ScreenFooter>
        {/*
          One solid red, carrying the running figure the way the design draws it:
          `เพิ่มรายการ · ฿95`, one label with the separator inside it. This used
          to be the label and a `<Money>` as two flex children, so that the
          amount kept a formatter and tabular figures — but the separator then
          had nowhere to live, and it went missing. `q4.addToCartWithPrice` holds
          the whole shape, dot included, which also lets a translator move the
          figure or drop the dot; two flex children hard-code Thai word order in
          JSX. The two things the element was carrying are carried anyway:
          `money()` is the same formatter `Money` calls, and `.q4-add` sets
          `tabular-nums` on the button so the price does not jitter as the guest
          steps the quantity.
        */}
        {/*
          The held state is announced but not drawn (owner directive,
          2026-08-14): `ยังเลือกไม่ครบ` under the button was a second sentence
          saying what the Palm Sugar `ต้องเลือก` pill on the unanswered group
          already says, and it said it at the bottom of the screen, furthest from
          the question. A screen reader still gets it as the button's
          description, because a pill further up the page is not something it can
          glance at.
        */}
        <Button
          className="q4-add"
          variant="primary"
          disabled={unmetGroups.length > 0}
          disabledReason={t('q4.requiredUnmet')}
          reasonVisible={false}
          onClick={onAdd}>
          {t('q4.addToCartWithPrice', {price: money(runningSatang)})}
        </Button>
      </ScreenFooter>
    </Shell>
  );
}

/**
 * The photo, if there is one and it works.
 *
 * Everything about this component is written from the assumption that it will
 * render nothing. `image_url` is usually absent, and when it is present it is a
 * merchant upload on an unreliable link over 4G — so a load failure is not an
 * exception, it is Tuesday. A failure removes the element entirely and the
 * typographic head below becomes the whole design again, which is what it was
 * built to be.
 *
 * `Photo` owns the geometry, the placeholder stripe and the empty `alt`; what it
 * does not own is the retreat, because it takes no `onError`. Hence the wrapper
 * and the **capture-phase** listener: `error` on an `<img>` does not bubble, so
 * a React `onError` on the parent would never fire, but every event still has a
 * capture phase on its ancestors and that one does. Reported to the foundation —
 * a photo that cannot say it failed is a photo every screen has to wrap.
 */
function ItemPhoto({src}: {src: string | null | undefined}) {
  const [failed, setFailed] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  useEffect(() => setFailed(false), [src]);

  useEffect(() => {
    const node = holder.current;
    if (!node) return;
    const onError = () => setFailed(true);
    node.addEventListener('error', onError, true);
    return () => node.removeEventListener('error', onError, true);
  }, [src]);

  if (!src || failed) return null;

  return (
    <div ref={holder}>
      <Photo src={src} shape="hero" />
    </div>
  );
}

/**
 * One modifier group: a heading, the state of the rule, the rule in words, and
 * the rows.
 *
 * `role="group"` with `aria-labelledby` rather than a `<fieldset>`: the shared
 * `SectionHeader` renders a real `<h2>` that a screen reader can navigate a long
 * item page by, and a `<legend>` may not contain the flex row that header is. The
 * grouping semantics are identical; the heading semantics are only available
 * this way round.
 */
function ModifierGroupSection({
  group,
  chosen,
  onToggle,
  onStep,
}: {
  group: ModifierGroup;
  /**
   * The **effective** selection — a modifier that sold out while ticked is
   * already gone from it. The row still renders, unticked and labelled, so the
   * guest sees what happened instead of paying for a silent substitution.
   */
  chosen: Record<string, number> | undefined;
  onToggle: (group: ModifierGroup, modifier: Modifier) => void;
  onStep: (group: ModifierGroup, modifier: Modifier, delta: number) => void;
}) {
  const {t, ln} = useI18n();
  const headingId = useId();

  const required = group.selection.min_select > 0;
  const count = groupCount(chosen);
  const unmet = count < group.selection.min_select;
  const atMax = count >= group.selection.max_select;
  const rule = selectionRule(group);
  const single = group.selection.max_select === 1 && !group.selection.allow_repeat;

  /**
   * The state pill the design puts at the right of the group heading.
   *
   * The design flips a required group's pill to Pandan the moment a spice level
   * is chosen, which is the one place the package breaks its own money rule: a
   * radio button being answered is not verified money. So the label never
   * changes and only the tone does — Palm Sugar while the question is open,
   * neutral once it is answered — and an optional group says how much room is
   * left instead.
   */
  const pill = required ? (
    <StatusPill tone={unmet ? 'warning' : 'neutral'}>{t('q4.required')}</StatusPill>
  ) : (
    <StatusPill>{t(atMax && !single ? 'q4.maxReached' : 'q4.optional')}</StatusPill>
  );

  return (
    <section className="q4-group" role="group" aria-labelledby={headingId}>
      <SectionHeader title={<span id={headingId}>{ln(group.name)}</span>} trailing={pill} />
      <OptionGroup helper={t(rule.key, rule.params)}>
        {group.modifiers.map((modifier) => (
          <ModifierRow
            key={modifier.modifier_id}
            group={group}
            modifier={modifier}
            quantity={chosen?.[modifier.modifier_id] ?? 0}
            groupCountNow={count}
            onToggle={onToggle}
            onStep={onStep}
            label={ln(modifier.name)}
          />
        ))}
      </OptionGroup>
    </section>
  );
}

function ModifierRow({
  group,
  modifier,
  quantity,
  groupCountNow,
  onToggle,
  onStep,
  label,
}: {
  group: ModifierGroup;
  modifier: Modifier;
  quantity: number;
  /** How much of `max_select` the whole group has already spent. */
  groupCountNow: number;
  onToggle: (group: ModifierGroup, modifier: Modifier) => void;
  onStep: (group: ModifierGroup, modifier: Modifier, delta: number) => void;
  label: string;
}) {
  const {t} = useI18n();

  const selectable = isModifierSelectable(modifier);
  const repeat = group.selection.allow_repeat === true;
  const single = group.selection.max_select === 1 && !repeat;
  /** This row cannot be *added* because the group is full. Never true of a chosen row. */
  const blockedByMax = groupCountNow >= group.selection.max_select && quantity === 0 && !single;
  /**
   * A radio only when the group is **required**.
   *
   * A radio group has no "none" — clicking the checked radio changes nothing, so
   * the browser fires no `change` and React fires no `onChange`, and the
   * un-choose branch in `toggle()` was unreachable. An optional extra a guest
   * could add and never remove is worse than an ugly control: the only way out
   * was to leave the screen. Mutual exclusion is enforced in `toggle()` (the
   * `single` branch replaces the whole group), not by the input type, so the
   * checkbox behaves identically while still being un-tickable — and it is the
   * honest shape, because a control that says "one of these, or none" is not a
   * radio group in the first place.
   */
  const radio = single && group.selection.min_select > 0;
  const delta = modifier.price_delta_satang;
  // `MoneyDelta` renders `+฿15` / `−฿5` — a leading sign and a true minus, which
  // is the design prototype's `฿-5` bug fixed in one place. Zero is not a delta
  // worth a column, so it renders nothing at all rather than `฿0`.
  const price = delta === 0 ? null : <MoneyDelta satang={delta} />;
  const unavailable = selectable ? null : (
    <span className="q4-unavailable pk-hint">{t('q4.modifierUnavailable')}</span>
  );

  if (repeat) {
    /**
     * A repeatable modifier: three shots of espresso, not three kinds of one.
     *
     * `max` is what the *group* has left plus what this row already holds, so
     * `+` goes inert at `max_select` even on a row that is already chosen —
     * which is the tap a per-row "is this one picked" guard never sees. `−` stays
     * live at every quantity above zero, because getting back under the limit is
     * the way out. Guarded a second time in `step()`, because "the button was
     * disabled" is a claim about a render and `max_select` is a claim about the
     * order that reaches the kitchen.
     */
    const remaining = Math.max(0, group.selection.max_select - groupCountNow);
    return (
      <div className="q4-repeat">
        <span className="q4-repeat-name">
          {label}
          {unavailable}
        </span>
        {price}
        <QtyStepper
          value={quantity}
          min={0}
          max={selectable ? quantity + remaining : quantity}
          onChange={(next) => onStep(group, modifier, next - quantity)}
          decreaseLabel={`${t('q4.decrease')} · ${label}`}
          increaseLabel={`${t('q4.increase')} · ${label}`}
          valueLabel={`${t('common.quantity')} · ${label}`}
        />
      </div>
    );
  }

  return (
    <OptionRow
      type={radio ? 'radio' : 'checkbox'}
      name={group.modifier_group_id}
      value={modifier.modifier_id}
      checked={quantity > 0}
      disabled={!selectable || blockedByMax}
      // Only for the cap. An unavailable modifier already says so in the visible
      // label, and announcing it twice is a row a screen reader reads at double
      // length for no extra fact.
      disabledReason={blockedByMax && selectable ? t('q4.maxReached') : undefined}
      onChange={() => onToggle(group, modifier)}
      label={
        <>
          {label}
          {unavailable}
        </>
      }
      note={price}
    />
  );
}

/**
 * The frame every state of this screen shares.
 *
 * The back control is an explicit, in-screen one because **iOS has no system
 * back gesture** (foundations §15) and this screen is reached from the menu on
 * every single order. The language link is here for the same kind of reason:
 * 03 §Q2 requires the switcher to be reachable from every screen without losing
 * the cart, and a tourist who cannot read the modifier group they are being
 * asked to choose from is stuck on precisely this screen.
 */
function Shell({children}: {children: ReactNode}) {
  const {t} = useI18n();
  return (
    <Screen>
      <Q4Styles />
      <TopBar
        leading={<BackLink to={ROUTES.menu} label={t('common.back')} />}
        trailing={<LanguageToggle variant="link" />}
      />
      {children}
    </Screen>
  );
}

/**
 * What is left of this screen's own stylesheet.
 *
 * It used to carry the − / + geometry and the primary button. Both moved into
 * `src/ui`: `QtyStepper` owns the 48px floor on both axes and the argument for
 * it (a `min-width: 44px` on exactly these two buttons once removed a paid
 * modifier from a real bill when a thumb slipped left at the cap), and
 * `test/guest-defects.test.tsx` still asserts it through `getComputedStyle` on
 * the rendered control rather than by grepping a string. What remains is layout
 * that belongs to this screen and nowhere else.
 *
 * `href` + `precedence` are React 19's stylesheet de-duplication, the same
 * mechanism `UiStyles` uses: one element in `<head>` however many times this
 * renders, and visible to jsdom, which an imported `.css` file is not.
 */
function Q4Styles() {
  return (
    <style href="pokpok-q4" precedence="pokpok-ui">
      {Q4_CSS}
    </style>
  );
}

/*
 * No comments inside the literal below: it is a JS string, so esbuild minifies
 * around it and never inside it, and every byte written between the backticks
 * ships to a guest on 4G. The reasoning lives here instead.
 *
 * `.q4-add` — the running price is now part of the button's own label string
 * rather than a `<Money>` element inside it, which took two problems away and
 * left one. Gone: `.pk-money--neutral` painted the figure in
 * `--color-text-primary`, Squid Ink on Chili Red at about 2.3:1, so the rule
 * here had to force it back to `inherit` on both the accent fill and the
 * desaturated disabled one. A plain label inherits `--color-on-accent` with no
 * help. Left: `.pk-money` was also where the figure got `tabular-nums`, and a
 * price that reflows on every tap of the quantity stepper is the jitter that
 * class exists to stop — so the button carries it instead. The old rule was
 * reported upstream as a foundation gap (every screen that puts a total inside
 * its primary action has it); that report still stands for the screens that do.
 *
 * `.q4-repeat .pk-money` — a modifier's price delta is River Stone beside its
 * label, the way `.pk-option-note` already paints the same column on the
 * checkbox rows. Written as a descendant selector rather than a class on the
 * element so it beats `.pk-money--neutral` on specificity instead of on the
 * order two `<style>` elements happen to land in.
 */
const Q4_CSS = `
.q4-article {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-6);
}
.q4-head {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--pokpok-space-4);
}
/* The name takes the room and wraps; the price never does. */
.q4-head-name {
  min-width: 0;
}
.q4-head .pk-money {
  flex: 0 0 auto;
}
.q4-group {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}
.q4-repeat {
  display: flex;
  align-items: center;
  gap: var(--pokpok-space-4);
  min-height: 48px;
  padding: var(--pokpok-space-1) 0;
}
.q4-repeat-name {
  flex: 1 1 auto;
  min-width: 0;
}
.q4-repeat .pk-money {
  flex: 0 0 auto;
  color: var(--color-text-secondary);
}
.q4-unavailable {
  display: block;
}
.q4-add {
  font-variant-numeric: tabular-nums;
}
`;
